import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useObservableState } from 'observable-hooks';
import { map } from 'rxjs/operators';
import * as yup from 'yup';
import CropSelect from './crop-select';
import ProductionTypeSelect from './production-type-select';

import ClassifierGroup from '../../../enums/classifier-group';
import Classifier from '../../../enums/classifier';
import ClassifierPropertyKey from '../../../enums/classifier-property-key';

import Form, { SubmitType } from '../../../components/form';
import Link from '../../../components/form/elements/link';
import Watch from '../../../components/form/elements/watch';
import Disabled from '../../../components/form/elements/disabled';
import Button from '../../../components/form/elements/button';
import Text from '../../../components/form/elements/text';
import Select, {
  emptySelectOption,
} from '../../../components/form/elements/select';
import TextArea from '../../../components/form/elements/text-area';
import Amount from '../../../components/form/elements/amount';
import Notification from '../../../components/form/elements/notification';

import Paginate from '../../../interfaces/paginate';
import { Product } from '../../../interfaces/product';
import ClassifierValue from '../../../interfaces/classifierValue';

import { route } from '../../../services/utils';
import { useService } from '../../../services/services';
import { CROP_CHANGE_PLAN_VIEW } from '../../../router/routes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import css from './crop-select.module.css';
import { validateDistinctBreed } from './create';

export default function EditCropChangePlan() {
  const { t } = useTranslation();
  const history = useHistory();
  const { cropChangePlanService, classifierService } = useService();

  const transKeyPrefix = 'pages.crop.crop_change_plan.create.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const [showIntercropComponent, setShowIntercropComponent] = useState(false);
  const [showIntercultureComponent, setShowIntercultureComponent] =
    useState(false);
  const [showCropFields, setShowCropFields] = useState(false);
  const [showInterculture, setShowInterculture] = useState(true);

  const { farmId, season, id } = useParams<{
    farmId: string;
    season: string;
    id: string;
  }>();
  const [cropChangePlan] = useObservableState<any>(
    () =>
      cropChangePlanService.show(farmId, id).pipe(
        map((cropChangePlan: any) => {
          if (cropChangePlan.crop_work_done) {
            setShowCropFields(true);
          }

          if (cropChangePlan.interculture_work_done) {
            setShowInterculture(false);
          }

          if (cropChangePlan.intercrop_crop_id) {
            setShowIntercropComponent(true);
          }

          if (cropChangePlan.interculture_crop_id) {
            setShowIntercultureComponent(true);
          }

          if (
            cropChangePlan.crop_group_id ===
              cropChangePlan.interculture_crop_group_id &&
            cropChangePlan.crop_id === cropChangePlan.interculture_crop_id &&
            cropChangePlan.breed === cropChangePlan.interculture_breed
          ) {
            cropChangePlan.onlyInterculture = true;
          }

          return {
            id: cropChangePlan.id,
            fields: [
              {
                label: `${cropChangePlan.field?.lad_number}, ${cropChangePlan.field?.name}, ${cropChangePlan.field?.area} ha`,
                value: cropChangePlan.field?.id,
                area: cropChangePlan.field?.area,
                lad_number: cropChangePlan.field?.lad_number,
                name: cropChangePlan.field?.name,
                season_id: cropChangePlan.field?.season_id,
                descendants: cropChangePlan.field?.descendants,
                active: cropChangePlan.field?.active,
              },
            ],
            area: cropChangePlan.field?.area,
            seasons: [
              {
                label: t(transKeyPrefix.concat('plan_season'), {
                  season: cropChangePlan.season_id,
                }),
                value: cropChangePlan.season_id,
              },
            ],
            crop_group_id: cropChangePlan.onlyInterculture
              ? { value: cropChangePlan.crop_group_id }
              : {
                  label: cropChangePlan.crop_group_value,
                  value: cropChangePlan.crop_group_id,
                },
            crop_id: cropChangePlan.onlyInterculture
              ? { value: cropChangePlan.crop_id }
              : {
                  label: cropChangePlan.crop_value,
                  value: cropChangePlan.crop_id,
                  crop_group: {
                    label: cropChangePlan.crop_group_value,
                    id: cropChangePlan.crop_group_id,
                  },
                },
            breed: cropChangePlan.onlyInterculture
              ? {
                  value: cropChangePlan.breed_id,
                  vlabel: cropChangePlan.breed,
                }
              : {
                  label: cropChangePlan.breed,
                  value: cropChangePlan.breed_id,
                  crop_group: {
                    label: cropChangePlan.crop_group_value,
                    id: cropChangePlan.crop_group_id,
                  },
                  crop: {
                    label: cropChangePlan.crop_value,
                    id: cropChangePlan.crop_id,
                  },
                },
            production_types: cropChangePlan.production_type_info
              ? cropChangePlan.production_type_info.map(
                  (productionType: any) => ({
                    production_type: {
                      label: productionType?.type_name ?? '',
                      value: productionType?.type_id ?? '',
                    },
                    yield: productionType?.yield ?? '',
                    yield_unit: {
                      label: productionType?.yield_unit ?? '',
                      value: productionType?.yield_unit ?? '',
                    },
                  })
                )
              : emptySelectOption,

            notes: cropChangePlan.notes ?? '',

            intercrop_crop_group_id: cropChangePlan.intercrop_crop_group_id
              ? {
                  label: cropChangePlan.intercrop_crop_group_value,
                  value: cropChangePlan.intercrop_crop_group_id,
                }
              : emptySelectOption,

            intercrop_crop_id: cropChangePlan.intercrop_crop_id
              ? {
                  label: cropChangePlan.intercrop_crop_value,
                  value: cropChangePlan.intercrop_crop_id,
                  crop_group: {
                    label: cropChangePlan.intercrop_crop_group_value,
                    id: cropChangePlan.intercrop_crop_group_id,
                  },
                }
              : emptySelectOption,

            intercrop_breed: cropChangePlan.intercrop_breed
              ? {
                  label: cropChangePlan.intercrop_breed,
                  value: cropChangePlan.intercrop_breed_id,
                  crop_group: {
                    label: cropChangePlan.intercrop_crop_group_value,
                    id: cropChangePlan.intercrop_crop_group_id,
                  },
                  crop: {
                    label: cropChangePlan.intercrop_crop_value,
                    id: cropChangePlan.intercrop_crop_id,
                  },
                }
              : emptySelectOption,

            interculture_crop_group_id:
              cropChangePlan.interculture_crop_group_id
                ? {
                    label: cropChangePlan.interculture_crop_group_value,
                    value: cropChangePlan.interculture_crop_group_id,
                  }
                : emptySelectOption,

            interculture_crop_id: cropChangePlan.interculture_crop_id
              ? {
                  label: cropChangePlan.interculture_crop_value,
                  value: cropChangePlan.interculture_crop_id,
                  crop_group: {
                    label: cropChangePlan.interculture_crop_group_value,
                    id: cropChangePlan.interculture_crop_group_id,
                  },
                }
              : emptySelectOption,

            interculture_breed: cropChangePlan.interculture_breed
              ? {
                  label: cropChangePlan.interculture_breed,
                  value: cropChangePlan.interculture_breed_id,
                  crop_group: {
                    label: cropChangePlan.interculture_crop_group_value,
                    id: cropChangePlan.interculture_crop_group_id,
                  },
                  crop: {
                    label: cropChangePlan.interculture_crop_value,
                    id: cropChangePlan.interculture_crop_id,
                  },
                }
              : emptySelectOption,

            status: cropChangePlan.status ?? '',
          };
        })
      ),
    null
  );

  const [bodyElements, setBodyElements] = useState([]);

  const checkFieldExistance = useCallback(
    (selectedFields: any, selectedSeasons: any) => {
      // recursively go through field descendants to check if the same field exists in given season
      const findFieldInSeason = (field: any, seasonId: any): any | null => {
        if (field.season_id === seasonId && field.active) {
          return field;
        } else if (field.descendants?.length > 0) {
          return findFieldInSeason(field.descendants[0], seasonId);
        } else {
          return null;
        }
      };

      let fieldErrorText = '';

      if (selectedSeasons && selectedFields) {
        selectedFields.forEach((field: any) => {
          selectedSeasons.forEach((season: any) => {
            const fieldInSelectedSeason = findFieldInSeason(
              field,
              parseInt(season.value)
            );

            if (!fieldInSelectedSeason) {
              fieldErrorText += t(transKeyPrefix.concat('field_error_text'), {
                fieldName: field.name,
                seasonId: season.value,
              });
            }
          });
        });
      }

      return fieldErrorText;
    },
    [t]
  );

  const schema = yup.object().shape({
    fields: yup
      .array()
      .when('seasons', {
        is: (val: any) => val && val.length > 0,
        then: yup.array().test((fields: any, context: any) => {
          const errorMessage = checkFieldExistance(
            fields,
            context.parent.season
          );

          if (errorMessage) {
            return context.createError({
              message: tp('field_error') + errorMessage,
            });
          }

          return true;
        }),
      })
      .required()
      .of(
        yup.object().shape({
          value: yup.string().required(),
        })
      )
      .min(1),
    area: yup.string(),
    seasons: yup
      .array()
      .required()
      .of(
        yup.object().shape({
          value: yup.string().required(),
        })
      )
      .min(1),
    crop_group_id: yup
      .object()
      .shape({
        value: yup.string().uuid().required(),
      })
      .required(),
    crop_id: yup
      .object()
      .shape({
        value: yup.string().uuid().required(),
      })
      .required(),
    breed: yup
      .object()
      .nullable()
      .shape({
        value: yup.string().when('breed', {
          is: (breed) => breed !== null && breed !== undefined,
          then: yup.string().required(),
          otherwise: yup.string().nullable(),
        }),
      })
      .test((breed: any, context: any) => {
        return validateDistinctBreed(context);
      }),
    production_types: yup.array().of(
      yup.object().shape({
        production_type: yup.object().shape({
          label: yup.string().nullable(),
          value: yup.string().nullable(),
        }),
        yield: yup.string().nullable(),
        yield_unit: yup
          .object()
          .nullable()
          .shape({
            label: yup.string(),
          })
          .nullable(),
      })
    ),
    notes: yup.string(),
    interculture_breed: yup
      .object()
      .shape({
        label: yup.string().nullable(),
        value: yup.string().nullable(),
      })
      .test((intercultureBreed: any, context: any) => {
        return validateDistinctBreed(context);
      }),
    intercrop_breed: yup
      .object()
      .shape({
        label: yup.string().nullable(),
        value: yup.string().nullable(),
      })
      .test((intercropBreed: any, context: any) => {
        return validateDistinctBreed(context);
      }),
    status: yup.string(),
  });

  const submit = useCallback<SubmitType<any>>(
    (form) => {
      form.fields = form.fields.map((field: any) => field.value);
      form.seasons = form.seasons.map((season: any) => season.value);
      form.crop_group_id = form.crop_group_id?.value;
      form.crop_id = form.crop_id?.value;
      form.breed = form.onlyInterculture
        ? form.interculture_breed?.value
        : form.breed?.value;

      form.production_types = form.production_types.map(
        (productionTypeInfo: any) => {
          if (productionTypeInfo?.production_type?.value) {
            return {
              type_id: productionTypeInfo.production_type?.value,
              type_name: productionTypeInfo.production_type?.label,
              yield: productionTypeInfo.yield,
              yield_unit: productionTypeInfo.yield_unit?.label,
            };
          } else {
            return null;
          }
        }
      );

      form.intercrop_crop_group_id = showIntercropComponent
        ? form.intercrop_crop_group_id?.value
        : '';
      form.intercrop_crop_id = showIntercropComponent
        ? form.intercrop_crop_id?.value
        : '';
      form.intercrop_breed = form.intercrop_breed?.value;

      form.interculture_crop_group_id = showIntercultureComponent
        ? form.interculture_crop_group_id?.value
        : '';
      form.interculture_crop_id = showIntercultureComponent
        ? form.interculture_crop_id?.value
        : '';
      form.interculture_breed = form.interculture_breed?.value;

      delete form.field;
      delete form.crop;
      delete form.crop_group;
      delete form.breed_id;
      delete form.crop_group_value;
      delete form.crop_value;
      delete form.crop_work_done;
      delete form.field_id;
      delete form.production_type_info;

      return [
        cropChangePlanService.store(farmId, form),
        (response) =>
          history.push(
            route(CROP_CHANGE_PLAN_VIEW.concat('?fieldIds=:fieldIds'), {
              farmId: response.cropChangePlan.farm_id,
              season: response.cropChangePlan.season_id,
              id: response.cropChangePlan.id,
              fieldIds: response.fieldIds,
            })
          ),
      ];
    },
    [
      cropChangePlanService,
      history,
      farmId,
      showIntercultureComponent,
      showIntercropComponent,
    ]
  );

  useEffect(() => {
    if (cropChangePlan !== null) {
      setBodyElements([
        Notification(tp('notification_create_work'), !showCropFields),
        Watch(
          Select('fields', {
            asyncOptions: (value, loadedOptions, { page, getValues }) =>
              cropChangePlanService
                .index(farmId, season, {
                  page,
                  active: 'yes',
                })
                .pipe(
                  map((response: Paginate<any>) => {
                    return {
                      hasMore: response.last_page > response.current_page,
                      options: response.data.map((v) => ({
                        label: `${v.lad_number}, ${v.name}, ${v.area} ha`,
                        value: v.id,
                        area: v.area,
                        lad_number: v.lad_number,
                        name: v.name,
                        season_id: v.season_id,
                        descendants: v.descendants,
                        active: v.active,
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                ),
            multi: true,
            label: tp('field_names'),
          }),
          ({ value, setValue, getValues }) => {
            let totalFieldArea = 0;

            value
              .filter((field: any) => field.area !== 0)
              .forEach((field: any) => (totalFieldArea += field.area));

            setValue('area', totalFieldArea);
          }
        ),
        Disabled(Text('area', tp('area'))),
        Select('seasons', {
          asyncOptions: (value, loadedOptions, { page, getValues }) =>
            cropChangePlanService.getAvailableSeasons(farmId, season).pipe(
              map((response: Paginate<any>) => {
                return {
                  hasMore: response.last_page > response.current_page,
                  options: response.data.map((v) => ({
                    label: v.title,
                    value: v.id,
                  })),
                  additional: { page: response.current_page + 1 },
                };
              })
            ),
          multi: true,
          label: tp('seasons'),
        }),
        Disabled(
          [
            Watch(
              Select('crop_group_id', {
                asyncOptions: (value, loadedOptions) => {
                  return cropChangePlanService
                    .productCategoriesWithTypes(farmId)
                    .pipe(
                      map((response: any) => ({
                        options: Object.values(response)
                          .map((v: any) => ({
                            label: v.cropGroup.label,
                            value: v.cropGroup.id,
                            product_ids: v.product_ids,
                          }))
                          .filter((value: any) => value.label !== 'Neattiecas'),
                      }))
                    );
                },
                multi: false,
                clearable: true,
                label: tp('crop_category'),
              }),
              ({ value, setValue, getValues }) => {
                if (showCropFields && value.value) {
                  const crop = getValues('crop_id');
                  const breed = getValues('breed');

                  if (value.value !== breed.cropGroup?.id) {
                    setValue('breed', emptySelectOption);
                  }

                  if (value.value !== crop.cropGroup?.id) {
                    setValue('crop_id', emptySelectOption);
                  }
                }
              }
            ),
            Watch(
              Select('crop_id', {
                asyncOptions: (value, loadedOptions, { page, getValues }) => {
                  const params: any = { page, value };

                  const cropGroupId = getValues('crop_group_id')?.value;

                  if (cropGroupId) {
                    params.crop_group = cropGroupId;
                  }

                  return classifierService
                    .valuesPaged(
                      ClassifierGroup.WAREHOUSE,
                      Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                      params
                    )
                    .pipe(
                      map((response: Paginate<ClassifierValue>) => {
                        return {
                          hasMore: response.last_page > response.current_page,
                          options: response.data.map((v) => ({
                            label: v.value,
                            value: v.id,
                            cropGroup: Object.values(v.properties)
                              .filter(
                                (p) =>
                                  p.property.key ===
                                  ClassifierPropertyKey.CROP_GROUP
                              )
                              .map((p) => {
                                const pv = p.value.pop();
                                return { id: pv.id, label: pv.label };
                              })
                              .pop(),
                          })),
                          additional: { page: response.current_page + 1 },
                        };
                      })
                    );
                },
                cacheOn: ['crop_group_id'],
                multi: false,
                clearable: true,
                label: tp('crop_type'),
              }),
              ({ value, setValue, getValues }) => {
                if (showCropFields && value.value) {
                  const cropGroup = getValues('crop_group_id');
                  const breed = getValues('breed');

                  if (value.cropGroup?.id !== cropGroup) {
                    setValue('crop_group_id', {
                      value: value.cropGroup?.id ?? '',
                      label: value.cropGroup?.label ?? '',
                      product_ids: [],
                    });
                  }

                  if (value.value !== breed.crop?.id) {
                    setValue('breed', emptySelectOption);
                  }
                }
              }
            ),
            Watch(
              Select('breed', {
                asyncOptions: (value, loadedOptions, { page, getValues }) => {
                  const params: any = { page, value };
                  const cropGroupId = getValues('crop_group_id')?.value;

                  params.product_categories = getValues('crop_id')?.value;
                  params.unique_values = true;

                  return cropChangePlanService
                    .getUniqueProducts(farmId, params)
                    .pipe(
                      map((response: Paginate<Product>) => ({
                        hasMore: response.last_page > response.current_page,
                        options: response.data
                          .map((product: Product) => ({
                            label: product.name,
                            value: product.id,
                            product: product,
                            crop: {
                              label: product.category?.value,
                              id: product.category?.id,
                            },
                            cropGroup: Object.values(
                              product.category.properties
                            ).find(
                              (property: any) =>
                                property.property.key ===
                                ClassifierPropertyKey.CROP_GROUP
                            )?.value[0],
                          }))
                          .filter((value: any) => {
                            if (cropGroupId && value.cropGroup) {
                              return value.cropGroup.id === cropGroupId;
                            } else {
                              return value.cropGroup?.label !== 'Neattiecas';
                            }
                          }),
                        additional: { page: response.current_page + 1 },
                      }))
                    );
                },
                cacheOn: ['crop_group_id', 'crop_id'],
                multi: false,
                creatable: true,
                clearable: true,
                label: tp('crop'),
              }),
              ({ value, setValue, getValues }) => {
                if (showCropFields && value.product) {
                  const cropGroup = getValues('crop_group_id');
                  const crop = getValues('crop_id');

                  if (value.cropGroup?.id !== cropGroup?.value) {
                    setValue('crop_group_id', {
                      value: value.cropGroup?.id ?? '',
                      label: value.cropGroup?.label ?? '',
                      product_ids: [],
                    });
                  }

                  if (value.crop?.id !== crop?.value) {
                    setValue('crop_id', {
                      value: value.crop?.id ?? '',
                      label: value.crop?.label ?? '',
                      cropGroup: {
                        id: value.cropGroup?.id ?? '',
                        label: value.cropGroup?.label ?? '',
                      },
                    });
                  }
                }
              }
            ),
          ],
          {
            watchOn: ['breed'],
            callback: () => (showCropFields ? false : true),
          }
        ),
        ProductionTypeSelect(
          () => [
            Disabled(
              Select('production_type', {
                asyncOptions: (value, loadedOptions, { page, getValues }) =>
                  classifierService
                    .valuesPaged(
                      ClassifierGroup.WAREHOUSE,
                      Classifier.PRODUCT_TYPES,
                      {
                        page,
                        value,
                        parent_id:
                          getValues('crop_id.value') ||
                          getValues('crop_group_id')?.product_ids,
                      }
                    )
                    .pipe(
                      map((response: Paginate<ClassifierValue>) => {
                        return {
                          hasMore: response.last_page > response.current_page,
                          options: response.data
                            .map((v) => ({
                              label: v.value,
                              value: v.id,
                              units:
                                Object.values(v.properties).find(
                                  (property: any) =>
                                    property.property?.key ===
                                    ClassifierPropertyKey.UNITS_OF_MEASURE
                                )?.value ?? [],
                            }))
                            .filter((value) => {
                              return (
                                value.label !== 'Neattiecas' &&
                                !getValues('production_types').some(
                                  (productionType: any) =>
                                    productionType.production_type.value ===
                                    value.value
                                )
                              );
                            }),
                          additional: { page: response.current_page + 1 },
                        };
                      })
                    ),
                cacheOn: ['/crop_group_id', '/crop_id'],
                multi: false,
                label: tp('planned_product'),
              }),
              {
                watchOn: '/crop_id',
                callback: (crop_id) => (crop_id?.value ? false : true),
              }
            ),
            Disabled(
              Amount(
                { amount: 'yield', unit: 'yield_unit' },
                {
                  asyncOptions: async (
                    value,
                    loadedOptions,
                    { parentName, getValues }
                  ) => {
                    return {
                      hasMore: false,
                      options:
                        getValues(
                          parentName
                            ? parentName.concat('.production_type')
                            : 'production_type'
                        )?.units?.map((v: string) => ({
                          label: v,
                          value: v,
                        })) ?? [],
                      additional: {},
                    };
                  },
                  clearable: true,
                  cacheOn: ['production_type'],
                  label: tp('yield'),
                  explanation: tp('explanation'),
                }
              ),
              {
                watchOn: 'production_type',
                callback: (production_type) =>
                  production_type?.value ? false : true,
              }
            ),
          ],
          () => [
            Disabled(
              Select('production_type', {
                asyncOptions: (value, loadedOptions, { page, getValues }) =>
                  classifierService
                    .valuesPaged(
                      ClassifierGroup.WAREHOUSE,
                      Classifier.PRODUCT_TYPES,
                      {
                        page,
                        value,
                        parent_id:
                          getValues('crop_id.value') ||
                          getValues('crop_group_id')?.product_ids,
                      }
                    )
                    .pipe(
                      map((response: Paginate<ClassifierValue>) => {
                        return {
                          hasMore: response.last_page > response.current_page,
                          options: response.data
                            .map((v) => ({
                              label: v.value,
                              value: v.id,
                              units:
                                Object.values(v.properties).find(
                                  (property: any) =>
                                    property.property?.key ===
                                    ClassifierPropertyKey.UNITS_OF_MEASURE
                                )?.value ?? [],
                            }))
                            .filter((value) => {
                              return (
                                value.label !== 'Neattiecas' &&
                                !getValues('production_types').some(
                                  (productionType: any) =>
                                    productionType.production_type.value ===
                                    value.value
                                )
                              );
                            }),
                          additional: { page: response.current_page + 1 },
                        };
                      })
                    ),
                cacheOn: ['/crop_group_id', '/crop_id'],
                multi: false,
                label: tp('planned_byproduct'),
              }),
              {
                watchOn: '/crop_id',
                callback: (crop_id) => (crop_id?.value ? false : true),
              }
            ),
            Disabled(
              Amount(
                { amount: 'yield', unit: 'yield_unit' },
                {
                  asyncOptions: async (
                    value,
                    loadedOptions,
                    { parentName, getValues }
                  ) => {
                    return {
                      hasMore: false,
                      options:
                        getValues(
                          parentName
                            ? parentName.concat('.production_type')
                            : 'production_type'
                        )?.units?.map((v: string) => ({
                          label: v,
                          value: v,
                        })) ?? [],
                      additional: {},
                    };
                  },
                  clearable: true,
                  cacheOn: ['production_type'],
                  label: tp('yield'),
                }
              ),
              {
                watchOn: 'production_type',
                callback: (production_type) =>
                  production_type?.value ? false : true,
              }
            ),
          ],
          'production_types',
          tp('add_byproduct')
        ),
        TextArea('notes', tp('notes')),
        !showIntercropComponent
          ? () => (
              <div className={css['wrapper']}>
                <div className={css['add']}>
                  <button
                    type="button"
                    name="add-intercrop-button"
                    onClick={() => setShowIntercropComponent(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} /> {tp('add_intercrop')}
                  </button>
                </div>
              </div>
            )
          : showCropFields
          ? CropSelect(
              () => [
                () => (
                  <div className={css['header']}>
                    <h3 className={css['title']}>{tp('intercrop')}</h3>
                    <button
                      className={css['close']}
                      type="button"
                      name="remove-intercrop-button"
                      onClick={() => setShowIntercropComponent(false)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ),
                Watch(
                  Select('intercrop_crop_group_id', {
                    asyncOptions: (value, loadedOptions, { page, getValues }) =>
                      classifierService
                        .valuesPaged(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.CROP_GROUPS,
                          {
                            page,
                            value,
                          }
                        )
                        .pipe(
                          map((response: Paginate<ClassifierValue>) => {
                            return {
                              hasMore:
                                response.last_page > response.current_page,
                              options: response.data
                                .map((v) => ({
                                  label: v.value,
                                  value: v.id,
                                }))
                                .filter(
                                  (value) => value.label !== 'Neattiecas'
                                ),
                              additional: { page: response.current_page + 1 },
                            };
                          })
                        ),
                    multi: false,
                    label: tp('crop_category'),
                  }),
                  ({ value, setValue, getValues }) => {
                    const crop = getValues('intercrop_crop_id');
                    const breed = getValues('intercrop_breed');

                    if (value.value) {
                      if (value.value !== breed.cropGroup?.id) {
                        setValue('intercrop_breed', emptySelectOption);
                      }

                      if (value.value !== crop.cropGroup?.id) {
                        setValue('intercrop_crop_id', emptySelectOption);
                      }
                    }
                  }
                ),
                Watch(
                  Select('intercrop_crop_id', {
                    asyncOptions: (
                      value,
                      loadedOptions,
                      { page, getValues }
                    ) => {
                      const params: any = { page, value };

                      const cropGroupId = getValues(
                        'intercrop_crop_group_id'
                      )?.value;

                      if (cropGroupId) {
                        params.crop_group = cropGroupId;
                      }

                      return classifierService
                        .valuesPaged(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                          params
                        )
                        .pipe(
                          map((response: Paginate<ClassifierValue>) => {
                            return {
                              hasMore:
                                response.last_page > response.current_page,
                              options: response.data.map((v) => ({
                                label: v.value,
                                value: v.id,
                                cropGroup: Object.values(v.properties)
                                  .filter(
                                    (p) =>
                                      p.property.key ===
                                      ClassifierPropertyKey.CROP_GROUP
                                  )
                                  .map((p) => {
                                    const pv = p.value.pop();
                                    return { id: pv.id, label: pv.label };
                                  })
                                  .pop(),
                              })),
                              additional: { page: response.current_page + 1 },
                            };
                          })
                        );
                    },
                    cacheOn: ['intercrop_crop_group_id'],
                    multi: false,
                    label: tp('crop_type'),
                  }),
                  ({ value, setValue, getValues }) => {
                    const cropGroup = getValues('intercrop_crop_group_id');
                    const breed = getValues('intercrop_breed');

                    if (value.value) {
                      if (value.cropGroup?.id !== cropGroup) {
                        setValue('intercrop_crop_group_id', {
                          value: value.cropGroup?.id ?? '',
                          label: value.cropGroup?.label ?? '',
                        });
                      }

                      if (value.value !== breed.crop?.id) {
                        setValue('intercrop_breed', emptySelectOption);
                      }
                    }
                  }
                ),
                Watch(
                  Select('intercrop_breed', {
                    asyncOptions: (
                      value,
                      loadedOptions,
                      { page, getValues }
                    ) => {
                      const params: any = { page, value };
                      const cropGroupId = getValues(
                        'intercrop_crop_group_id'
                      )?.value;

                      params.product_categories =
                        getValues('intercrop_crop_id')?.value;
                      params.unique_values = true;

                      return cropChangePlanService
                        .getUniqueProducts(farmId, params)
                        .pipe(
                          map((response: Paginate<Product>) => ({
                            hasMore: response.last_page > response.current_page,
                            options: response.data
                              .map((product: Product) => ({
                                label: product.name,
                                value: product.id,
                                product: product,
                                crop: {
                                  label: product.category?.value,
                                  id: product.category?.id,
                                },
                                cropGroup: Object.values(
                                  product.category.properties
                                ).find(
                                  (property: any) =>
                                    property.property.key ===
                                    ClassifierPropertyKey.CROP_GROUP
                                )?.value[0],
                              }))
                              .filter((value: any) => {
                                if (cropGroupId && value.cropGroup) {
                                  return value.cropGroup.id === cropGroupId;
                                }

                                return value.cropGroup?.label !== 'Neattiecas';
                              }),
                            additional: { page: response.current_page + 1 },
                          }))
                        );
                    },
                    cacheOn: ['intercrop_crop_group_id', 'intercrop_crop_id'],
                    multi: false,
                    creatable: true,
                    clearable: true,
                    label: tp('crop'),
                  }),
                  ({ value, setValue, getValues }) => {
                    const cropGroup = getValues('intercrop_crop_group_id');
                    const crop = getValues('intercrop_crop_id');

                    if (value.product) {
                      if (value.cropGroup?.id !== cropGroup?.value) {
                        setValue('intercrop_crop_group_id', {
                          value: value.cropGroup?.id ?? '',
                          label: value.cropGroup?.label ?? '',
                        });
                      }

                      if (value.crop?.id !== crop?.value) {
                        setValue('intercrop_crop_id', {
                          value: value.crop?.id ?? '',
                          label: value.crop?.label ?? '',
                          cropGroup: {
                            id: value.cropGroup?.id ?? '',
                            label: value.cropGroup?.label ?? '',
                          },
                        });
                      }
                    }
                  }
                ),
              ],
              'intercrop-select',
              null,
              null,
              showIntercropComponent
            )
          : CropSelect(
              () => [
                () => (
                  <>
                    <div className={css['header']}>
                      <h3 className={css['title']}>{tp('intercrop')}</h3>
                      <button
                        className={css['close']}
                        type="button"
                        name="remove-intercrop-button"
                        onClick={() => setShowIntercropComponent(false)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </>
                ),
                Disabled(
                  Select('intercrop_crop_group_id', {
                    asyncOptions: (value, loadedOptions, { page, getValues }) =>
                      classifierService
                        .valuesPaged(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.CROP_GROUPS,
                          {
                            page,
                            value,
                          }
                        )
                        .pipe(
                          map((response: Paginate<ClassifierValue>) => {
                            return {
                              hasMore:
                                response.last_page > response.current_page,
                              options: response.data
                                .map((v) => ({
                                  label: v.value,
                                  value: v.id,
                                }))
                                .filter(
                                  (value) => value.label !== 'Neattiecas'
                                ),
                              additional: { page: response.current_page + 1 },
                            };
                          })
                        ),
                    multi: false,
                    label: tp('crop_category'),
                  })
                ),
                Disabled(
                  Select('intercrop_crop_id', {
                    asyncOptions: (
                      value,
                      loadedOptions,
                      { page, getValues }
                    ) => {
                      const params: any = { page, value };

                      const cropGroupId = getValues(
                        'intercrop_crop_group_id'
                      )?.value;

                      if (cropGroupId) {
                        params.crop_group = cropGroupId;
                      }

                      return classifierService
                        .valuesPaged(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                          params
                        )
                        .pipe(
                          map((response: Paginate<ClassifierValue>) => {
                            return {
                              hasMore:
                                response.last_page > response.current_page,
                              options: response.data.map((v) => ({
                                label: v.value,
                                value: v.id,
                                cropGroup: Object.values(v.properties).find(
                                  (p) =>
                                    p.property.key ===
                                    ClassifierPropertyKey.CROP_GROUP
                                ).value[0],
                              })),
                              additional: { page: response.current_page + 1 },
                            };
                          })
                        );
                    },
                    cacheOn: ['intercrop_crop_group_id'],
                    multi: false,
                    label: tp('crop_type'),
                  })
                ),
                Disabled(
                  Select('intercrop_breed', {
                    asyncOptions: (
                      value,
                      loadedOptions,
                      { page, getValues }
                    ) => {
                      const params: any = { page, value };
                      const cropGroupId = getValues(
                        'intercrop_crop_group_id'
                      )?.value;

                      params.product_categories =
                        getValues('intercrop_crop_id')?.value;
                      params.unique_values = true;

                      return cropChangePlanService
                        .getUniqueProducts(farmId, params)
                        .pipe(
                          map((response: Paginate<Product>) => ({
                            hasMore: response.last_page > response.current_page,
                            options: response.data
                              .map((product: Product) => ({
                                label: product.name,
                                value: product.id,
                                product: product,
                                crop: {
                                  label: product.category?.value,
                                  id: product.category?.id,
                                },
                                cropGroup: Object.values(
                                  product.category.properties
                                ).find(
                                  (property: any) =>
                                    property.property.key ===
                                    ClassifierPropertyKey.CROP_GROUP
                                )?.value[0],
                              }))
                              .filter((value: any) => {
                                if (cropGroupId && value.cropGroup) {
                                  return value.cropGroup.id === cropGroupId;
                                }

                                return value.cropGroup?.label !== 'Neattiecas';
                              }),
                            additional: { page: response.current_page + 1 },
                          }))
                        );
                    },
                    cacheOn: ['intercrop_crop_group_id', 'intercrop_crop_id'],
                    multi: false,
                    creatable: true,
                    clearable: true,
                    label: tp('crop'),
                  })
                ),
              ],
              'intercrop-select',
              null,
              null,
              showIntercropComponent,
              false
            ),
        !showIntercultureComponent
          ? () => (
              <div className={css['wrapper']}>
                <div className={css['add']}>
                  <button
                    type="button"
                    name="add-interculture-button"
                    onClick={() => setShowIntercultureComponent(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} /> {tp('add_interculture')}
                  </button>
                </div>
              </div>
            )
          : showInterculture
          ? CropSelect(
              () => [
                () => (
                  <div className={css['header']}>
                    <h3 className={css['title']}>{tp('interculture')}</h3>
                    <button
                      className={css['close']}
                      type="button"
                      name="remove-interculture-button"
                      onClick={() => setShowIntercultureComponent(false)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ),
                Watch(
                  Select('interculture_crop_group_id', {
                    asyncOptions: (value, loadedOptions, { page, getValues }) =>
                      classifierService
                        .valuesPaged(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.CROP_GROUPS,
                          {
                            page,
                            value,
                          }
                        )
                        .pipe(
                          map((response: Paginate<ClassifierValue>) => {
                            return {
                              hasMore:
                                response.last_page > response.current_page,
                              options: response.data
                                .map((v) => ({
                                  label: v.value,
                                  value: v.id,
                                }))
                                .filter(
                                  (value) => value.label !== 'Neattiecas'
                                ),
                              additional: { page: response.current_page + 1 },
                            };
                          })
                        ),
                    multi: false,
                    label: tp('crop_category'),
                  }),
                  ({ value, setValue, getValues }) => {
                    const crop = getValues('interculture_crop_id');
                    const breed = getValues('interculture_breed');

                    if (value.value) {
                      if (value.value !== breed.cropGroup?.id) {
                        setValue('interculture_breed', emptySelectOption);
                      }

                      if (value.value !== crop.cropGroup?.id) {
                        setValue('interculture_crop_id', emptySelectOption);
                      }
                    }
                  }
                ),
                Watch(
                  Select('interculture_crop_id', {
                    asyncOptions: (
                      value,
                      loadedOptions,
                      { page, getValues }
                    ) => {
                      const params: any = { page, value };

                      const cropGroupId = getValues(
                        'interculture_crop_group_id'
                      )?.value;

                      if (cropGroupId) {
                        params.crop_group = cropGroupId;
                      }

                      return classifierService
                        .valuesPaged(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                          params
                        )
                        .pipe(
                          map((response: Paginate<ClassifierValue>) => {
                            return {
                              hasMore:
                                response.last_page > response.current_page,
                              options: response.data.map((v) => ({
                                label: v.value,
                                value: v.id,
                                cropGroup: Object.values(v.properties)
                                  .filter(
                                    (p) =>
                                      p.property.key ===
                                      ClassifierPropertyKey.CROP_GROUP
                                  )
                                  .map((p) => {
                                    const pv = p.value.pop();
                                    return { id: pv.id, label: pv.label };
                                  })
                                  .pop(),
                              })),
                              additional: { page: response.current_page + 1 },
                            };
                          })
                        );
                    },
                    cacheOn: ['interculture_crop_group_id'],
                    multi: false,
                    label: tp('crop_type'),
                  }),
                  ({ value, setValue, getValues }) => {
                    const cropGroup = getValues('interculture_crop_group_id');
                    const breed = getValues('interculture_breed');

                    if (value.value) {
                      if (value.cropGroup?.id !== cropGroup) {
                        setValue('interculture_crop_group_id', {
                          value: value.cropGroup?.id ?? '',
                          label: value.cropGroup?.label ?? '',
                        });
                      }

                      if (value.value !== breed.crop?.id) {
                        setValue('interculture_breed', emptySelectOption);
                      }
                    }
                  }
                ),
                Watch(
                  Select('interculture_breed', {
                    asyncOptions: (
                      value,
                      loadedOptions,
                      { page, getValues }
                    ) => {
                      const params: any = { page, value };
                      const cropGroupId = getValues(
                        'interculture_crop_group_id'
                      )?.value;

                      params.product_categories = getValues(
                        'interculture_crop_id'
                      )?.value;
                      params.unique_values = true;

                      return cropChangePlanService
                        .getUniqueProducts(farmId, params)
                        .pipe(
                          map((response: Paginate<Product>) => ({
                            hasMore: response.last_page > response.current_page,
                            options: response.data
                              .map((product: Product) => ({
                                label: product.name,
                                value: product.id,
                                product: product,
                                crop: {
                                  label: product.category?.value,
                                  id: product.category?.id,
                                },
                                cropGroup: Object.values(
                                  product.category.properties
                                ).find(
                                  (property: any) =>
                                    property.property.key ===
                                    ClassifierPropertyKey.CROP_GROUP
                                )?.value[0],
                              }))
                              .filter((value: any) => {
                                if (cropGroupId && value.cropGroup) {
                                  return value.cropGroup.id === cropGroupId;
                                }

                                return value.cropGroup?.label !== 'Neattiecas';
                              }),
                            additional: { page: response.current_page + 1 },
                          }))
                        );
                    },
                    cacheOn: [
                      'interculture_crop_group_id',
                      'interculture_crop_id',
                    ],
                    multi: false,
                    creatable: true,
                    clearable: true,
                    label: tp('crop'),
                  }),
                  ({ value, setValue, getValues }) => {
                    const cropGroup = getValues('interculture_crop_group_id');
                    const crop = getValues('interculture_crop_id');

                    if (value.product) {
                      if (value.cropGroup?.id !== cropGroup?.value) {
                        setValue('interculture_crop_group_id', {
                          value: value.cropGroup?.id ?? '',
                          label: value.cropGroup?.label ?? '',
                        });
                      }

                      if (value.crop?.id !== crop?.value) {
                        setValue('interculture_crop_id', {
                          value: value.crop?.id ?? '',
                          label: value.crop?.label ?? '',
                          cropGroup: {
                            id: value.cropGroup?.id ?? '',
                            label: value.cropGroup?.label ?? '',
                          },
                        });
                      }
                    }
                  }
                ),
              ],
              'interculture-select',
              null,
              null,
              showIntercultureComponent
            )
          : CropSelect(
              () => [
                () => (
                  <>
                    <div className={css['header']}>
                      <h3 className={css['title']}>{tp('interculture')}</h3>
                    </div>
                  </>
                ),
                Disabled(
                  Select('interculture_crop_group_id', {
                    asyncOptions: (value, loadedOptions, { page, getValues }) =>
                      classifierService
                        .valuesPaged(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.CROP_GROUPS,
                          {
                            page,
                            value,
                          }
                        )
                        .pipe(
                          map((response: Paginate<ClassifierValue>) => {
                            return {
                              hasMore:
                                response.last_page > response.current_page,
                              options: response.data
                                .map((v) => ({
                                  label: v.value,
                                  value: v.id,
                                }))
                                .filter(
                                  (value) => value.label !== 'Neattiecas'
                                ),
                              additional: { page: response.current_page + 1 },
                            };
                          })
                        ),
                    multi: false,
                    label: tp('crop_category'),
                  })
                ),
                Disabled(
                  Select('interculture_crop_id', {
                    asyncOptions: (
                      value,
                      loadedOptions,
                      { page, getValues }
                    ) => {
                      const params: any = { page, value };

                      const cropGroupId = getValues(
                        'interculture_crop_group_id'
                      )?.value;

                      if (cropGroupId) {
                        params.crop_group = cropGroupId;
                      }

                      return classifierService
                        .valuesPaged(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                          params
                        )
                        .pipe(
                          map((response: Paginate<ClassifierValue>) => {
                            return {
                              hasMore:
                                response.last_page > response.current_page,
                              options: response.data.map((v) => ({
                                label: v.value,
                                value: v.id,
                                cropGroup: Object.values(v.properties).find(
                                  (p) =>
                                    p.property.key ===
                                    ClassifierPropertyKey.CROP_GROUP
                                ).value[0],
                              })),
                              additional: { page: response.current_page + 1 },
                            };
                          })
                        );
                    },
                    cacheOn: ['interculture_crop_group_id'],
                    multi: false,
                    label: tp('crop_type'),
                  })
                ),
                Disabled(
                  Select('interculture_breed', {
                    asyncOptions: (
                      value,
                      loadedOptions,
                      { page, getValues }
                    ) => {
                      const params: any = { page, value };
                      const cropGroupId = getValues(
                        'interculture_crop_group_id'
                      )?.value;

                      params.product_categories = getValues(
                        'interculture_crop_id'
                      )?.value;
                      params.unique_values = true;

                      return cropChangePlanService
                        .getUniqueProducts(farmId, params)
                        .pipe(
                          map((response: Paginate<Product>) => ({
                            hasMore: response.last_page > response.current_page,
                            options: response.data
                              .map((product: Product) => ({
                                label: product.name,
                                value: product.id,
                                product: product,
                                crop: {
                                  label: product.category?.value,
                                  id: product.category?.id,
                                },
                                cropGroup: Object.values(
                                  product.category.properties
                                ).find(
                                  (property: any) =>
                                    property.property.key ===
                                    ClassifierPropertyKey.CROP_GROUP
                                )?.value[0],
                              }))
                              .filter((value: any) => {
                                if (cropGroupId && value.cropGroup) {
                                  return value.cropGroup.id === cropGroupId;
                                }

                                return value.cropGroup?.label !== 'Neattiecas';
                              }),
                            additional: { page: response.current_page + 1 },
                          }))
                        );
                    },
                    cacheOn: [
                      'interculture_crop_group_id',
                      'interculture_crop_id',
                    ],
                    multi: false,
                    creatable: true,
                    clearable: true,
                    label: tp('crop'),
                  })
                ),
              ],
              'interculture-select',
              null,
              null,
              showIntercultureComponent,
              false
            ),
      ]);
    }
  }, [
    cropChangePlan,
    farmId,
    id,
    transKeyPrefix,
    setBodyElements,
    showCropFields,
    showIntercropComponent,
    showIntercultureComponent,
    showInterculture,
    classifierService,
    cropChangePlanService,
    season,
    tp,
  ]);

  const [footer, setFooter] = useState([]);

  useEffect(
    () =>
      setFooter(() => [
        Button('save'),
        Link(
          route(CROP_CHANGE_PLAN_VIEW.concat('?fieldIds=:fieldIds'), {
            farmId: farmId,
            season: season,
            id: id,
            fieldIds: [cropChangePlan?.field?.id],
          }),
          'cancel'
        ),
      ]),
    [cropChangePlan, farmId, season, id]
  );

  return (
    <Form
      mode={'edit'}
      title={tp('edit_plan')}
      body={bodyElements}
      footer={footer}
      values={cropChangePlan}
      submit={submit}
      schema={schema}
    />
  );
}
